import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HiOutlineChatBubbleLeftRight,
  HiOutlineNewspaper,
  HiOutlineShieldCheck,
  HiOutlineSpeakerWave,
  HiOutlineSpeakerXMark,
  HiOutlineSquaresPlus
} from 'react-icons/hi2';
import { ReviewIcon } from '../icons/ReviewIcon';
import { useKodi } from '../../providers/KodiProvider';
import { useAudio } from '../../providers/AudioProvider';
import { useOrg } from '../../providers/OrgProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { useAgentEntitlements } from '../../hooks/useAgentEntitlements';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  pathPattern?: string | string[]; // Substring pattern(s) matched against pathname in isActive
}

/**
 * Bottom navigation bar for mobile devices
 * Shows 5 main nav items in a fixed bar at the bottom
 */
export const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // V2 org state (from OrgProvider)
  const { currentOrg } = useOrg();
  const org_id = currentOrg?.org_id;

  // Get Kodi state for indicator
  const { isProcessing, hasUnreadResponse } = useKodi();
  const { isLiveActive, hasBeenLive, volume } = useAudio();
  const { isAdmin } = usePermissions();
  const { isFeatureEntitled } = useAgentEntitlements();

  // Build org-aware paths
  const feedPath = org_id ? `/v2/organizations/${org_id}/feed` : '/v2/feed';

  const navItems: NavItem[] = [
    ...(isFeatureEntitled('ask-kodi')
      ? [
          {
            id: 'assistant',
            label: 'KODI',
            path: '/v2/assistant',
            icon: <HiOutlineChatBubbleLeftRight className="w-6 h-6" />
          }
        ]
      : []),
    {
      id: 'feed',
      label: 'Live',
      path: feedPath,
      // The Live page serves at both /feed and /channels (the latter is the
      // default landing), so both must mark the nav item active (#324).
      pathPattern: ['/feed', '/channels'],
      icon: <HiOutlineSpeakerWave className="w-6 h-6" />
    },
    ...(isFeatureEntitled('reviews')
      ? [
          {
            id: 'analytics',
            label: 'Review',
            path: '/v2/dashboard',
            icon: <ReviewIcon className="w-6 h-6" />
          }
        ]
      : []),
    {
      id: 'subs',
      label: 'Subs',
      path: '/v2/subs',
      icon: <HiOutlineNewspaper className="w-6 h-6" />
    },
    {
      // Alerts lives under Agents (registry entry `alerts`) — reachable via
      // the Agents selector grid on mobile, no top-level item.
      id: 'agents',
      label: 'Agents',
      path: '/v2/agents',
      icon: <HiOutlineSquaresPlus className="w-6 h-6" />
    },
    ...(isAdmin
      ? [
          {
            id: 'settings',
            label: 'Admin',
            path: '/v2/settings',
            icon: <HiOutlineShieldCheck className="w-6 h-6" />
          }
        ]
      : [])
  ];

  const isActive = (item: NavItem) => {
    const { path, pathPattern } = item;
    if (location.pathname === path || location.pathname.startsWith(path + '/')) {
      return true;
    }
    if (pathPattern) {
      const patterns = Array.isArray(pathPattern) ? pathPattern : [pathPattern];
      if (patterns.some((p) => location.pathname.includes(p))) {
        return true;
      }
    }
    return false;
  };

  return (
    <nav
      aria-label="Main navigation"
      className="fixed bottom-0 left-0 right-0 h-16 bg-vb-surface border-t border-vb-border flex items-center justify-around px-2 dark:bg-vb-surface-dark dark:border-vb-border-dark z-50 safe-area-bottom"
    >
      {navItems.map((item) => {
        const active = isActive(item);
        const isKodi = item.id === 'assistant';
        const isFeed = item.id === 'feed';
        const showKodiIndicator = isKodi && (isProcessing || hasUnreadResponse);
        const kodiIndicatorColor = isProcessing ? 'bg-vb-primary' : 'bg-vb-danger';

        let feedIcon = item.icon;
        if (isFeed) {
          if (active && hasBeenLive && !isLiveActive) {
            feedIcon = <HiOutlineSpeakerWave className="w-6 h-6 text-vb-warning" />;
          } else if (hasBeenLive && volume > 0) {
            feedIcon = <HiOutlineSpeakerWave className="w-6 h-6 text-vb-success" />;
          } else {
            feedIcon = <HiOutlineSpeakerXMark className="w-6 h-6 text-vb-success" />;
          }
        }

        return (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            aria-current={active ? 'page' : undefined}
            className={`flex flex-col items-center justify-center flex-1 py-2 min-w-0 transition-colors ${
              isFeed && hasBeenLive && (volume > 0 || (active && !isLiveActive))
                ? ''
                : active
                  ? 'text-vb-primary'
                  : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
            }`}
          >
            <span className="relative">
              {feedIcon}
              {showKodiIndicator && (
                <span
                  className={`absolute -top-1 -right-1 w-2.5 h-2.5 ${kodiIndicatorColor} rounded-full animate-pulse`}
                />
              )}
            </span>
            <span className="text-[10px] mt-1 font-medium truncate max-w-full">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};
